import React, { useState } from 'react';
import { PageHeader } from '@/components/common/PageHeader';
import { SectionTitle } from '@/components/common/SectionTitle';
import { Card, CardContent } from '@/components/ui/card';
import { Calendar, Award, Users, BookOpen, CheckCircle } from 'lucide-react';

const ScholarshipPage = () => {

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    class: '',
    stream: '',
    school: '',
    city: '',
    slot: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    alert('PSAT Registration Submitted Successfully!');
  };

  return (
    <div>

      {/* HERO */}
      <PageHeader
        title="PSAT – Prayas Scholarship cum Admission Test"
        subtitle="Unlock your potential with up to 90% scholarship on IIT JEE & NEET coaching."
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'Scholarship' }
        ]}
      />

      {/* IMPORTANT DATES */}
      <section className="section-padding bg-surface-alt">
        <div className="container-custom">
          <SectionTitle
            title="Important Dates"
            description="Secure your seat before the deadline"
            className="mb-12"
          />

          <Card>
            <CardContent className="p-8 grid md:grid-cols-2 gap-6">

              <p className="flex items-center gap-2">
                <Calendar className="text-primary" />
                <strong>Registration Start Date:</strong> Open Now
              </p>

              <p className="flex items-center gap-2">
                <Calendar className="text-primary" />
                <strong>Registration Form:</strong>
                <a
                  href="https://docs.google.com/forms/d/e/1FAIpQLSde-HFwUaiXALrJXaIfY9P3MykaEfxDEQn3cmHTpSHwQZaIjg/viewform"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold underline hover:text-primary-dark"
                >
                  Click Here to Register
                </a>
              </p>

              <p className="flex items-center gap-2">
                <Calendar className="text-primary" />
                <strong>Examination Date:</strong> Every Saturday
              </p>

              <p className="flex items-center gap-2">
                <Calendar className="text-primary" />
                <strong>Exam Time:</strong> 2 PM – 4 PM
              </p>

              <p className="flex items-center gap-2">
                <Award className="text-primary" />
                <strong>Mode of Exam:</strong> Offline at Bhilwara Center
              </p>

              <p className="flex items-center gap-2">
                <Award className="text-primary" />
                <strong>Result Declaration:</strong> Monday Evening
              </p>

            </CardContent>
          </Card>
        </div>
      </section>


      {/* ELIGIBILITY */}
      <section className="section-padding">
        <div className="container-custom">
          <SectionTitle
            title="Eligibility Criteria"
            description="Open to ambitious students aiming for Engineering or Medical careers."
            className="mb-12"
          />

          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Moving to Class 11: Students currently in Class 10.",
              "Moving to Class 12: Students currently in Class 11.",
              "Targeting JEE/NEET: Dropper / Repeater students (Class 12 passed).",
              "Foundation: Students in Classes 7th to 10th looking for an early competitive edge."
            ].map((item, i) => (
              <Card key={i}>
                <CardContent className="p-6 flex gap-3">
                  <Users className="text-primary" />
                  {item}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* EXAM PROCESS */}
      <section className="section-padding bg-surface-alt">
        <div className="container-custom">
          <SectionTitle
            title="Exam & Selection Process"
            description="We follow a transparent, merit-based selection process."
            className="mb-12"
          />

          <div className="grid md:grid-cols-2 gap-6">
            {[
              "Mode: Offline examination.",
              "Format: Multiple Choice Questions (MCQs).",
              "Syllabus (Senior Classes 11/12/Droppers): Physics, Chemistry, Mathematics/Biology.",
              "Syllabus (Junior Classes): Mental Ability, Science, and Mathematics.",
              "Selection: Scholarships awarded based on marks achieved in PSAT.",
              "Top scores qualify for higher fee waivers — up to 90% discount."
            ].map((item, i) => (
              <Card key={i}>
                <CardContent className="p-6 flex gap-3">
                  <BookOpen className="text-primary" />
                  {item}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>


      {/* INSTRUCTIONS */}
      <section className="section-padding">
        <div className="container-custom">
          <SectionTitle
            title="Important Instructions for Students"
            description="Please read carefully before appearing for the test."
            className="mb-10"
          />

          <Card>
            <CardContent className="p-8 space-y-4">

              {[
                {
                  label: "Reporting Time:",
                  text: "Arrive at the exam center at least 30 minutes before the scheduled start."
                },
                {
                  label: "Documents Required:",
                  text: "Carry a valid School ID/Aadhaar Card."
                },
                {
                  label: "Prohibited Items:",
                  text: "No electronic gadgets, calculators, or smart watches are allowed inside the examination hall."
                },
                {
                  label: "Marking Scheme:",
                  text: "Mention if there is negative marking, e.g., '+4 for correct, -1 for incorrect.'"
                }
              ].map((item, i) => (
                <p key={i} className="flex gap-2">
                  <CheckCircle className="text-primary mt-1" size={18} />

                  <span>
                    <strong>{item.label}</strong> {item.text}
                  </span>
                </p>
              ))}

            </CardContent>

          </Card>
        </div>
      </section>


      {/* DIRECTOR MESSAGE */}
      <section className="section-padding bg-primary text-white">
        <div className="container-custom max-w-4xl text-center">

          <h2 className="text-3xl font-bold mb-6">
            A Message from the Director
          </h2>

          <p className="text-lg leading-relaxed mb-6">
            At Prayas Institute, we believe that financial constraints should never stand in the way of a brilliant mind.
            PSAT is more than just a scholarship test; it is an invitation to join a community dedicated to excellence.
            Challenge yourself and take the first step toward your dream college.
          </p>

          <p className="font-semibold">
            — Piyush Soni, Director & Mathematics Mentor
          </p>

        </div>
      </section>


      {/* READY SECTION */}
      <section className="section-padding bg-surface-alt">
        <div className="container-custom max-w-3xl text-center">

          <SectionTitle
            title="Ready to Start Your Journey?"
            description="Join hundreds of students who have chosen Prayas Institute to lead them to success in JEE & NEET."
            className="mb-8"
          />

          <div className="space-y-2 mb-6 text-left inline-block">
            <p><strong>• Step 1</strong>: Click the "Register for PSAT" button below.</p>
            <p><strong>• Step 2</strong>: Fill in your academic details.</p>
            <p><strong>• Step 3</strong>: Choose your preferred exam slot.</p>
          </div>

          <div className="flex gap-4 justify-center flex-wrap">
            <a href="/enquiry" className="bg-primary text-white px-6 py-3 rounded-lg font-semibold hover:bg-primary-dark transition">
              Register Now for PSAT 2026
            </a>

            <button className="border border-primary text-primary px-6 py-3 rounded-lg font-semibold hover:bg-primary hover:text-white transition">
              Download Sample Paper
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ScholarshipPage;
